import React from "react";
import { FieldType } from "../../model/service-request/attribute";
import { DynamicFieldProps, DynamicFieldItem } from "../../model/dynamic-forms";
import { Attribute, Option } from "../../model/service-request/attribute";
import Input from "../../common/components/new/ui/Input";
import Select, { SelectItem } from "../../common/components/new/select/Select";
import ToggleSlider from "../../common/components/xepp/toggle-slider/ToggleSlider";
import FileUploadInput from "../../common/components/new/file-upload/FileUploadInput";
import DatePicker from "../../common/components/new/datepicker/DatePicker";
import { getFileExtensionTypeString, FileExtensionType } from "../../model/service-request/attribute";
import { Money } from "../../model/common/money";
import ConditionalDropdown from "./components/ConditionalDropdown";

class DynamicFormGenerator {
	generateDynamicField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition } = props;

		switch (attributeDefinition.fieldType) {
			case FieldType.Text:
				return this.generateTextField(props);

			case FieldType.Number:
				return this.generateNumberField(props);

			case FieldType.Bool:
				return this.generateBooleanField(props);

			case FieldType.Dropdown:
				return this.generateDropdownField(props);

			case FieldType.ConditionalDropdown:
				return this.generateConditionalDropdownField(props);

			case FieldType.ChildDropdown:
				return this.displayNotImplementedText(attributeDefinition.fieldType!);

			case FieldType.DateTime:
				return this.generateDateTimeField(props);

			case FieldType.SingleSelect:
				return this.generateSingleSelectField(props);

			case FieldType.MultiSelect:
				return this.displayNotImplementedText(attributeDefinition.fieldType!);

			case FieldType.TextDescription:
				return this.generateTextDescriptionField(props);

			case FieldType.File:
				return this.generateFileField(props);

			case FieldType.Image:
				return this.displayNotImplementedText(attributeDefinition.fieldType!);

			default:
				return null;
		}
	}

	private generateTextField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		const initialValue = preselectedFormValue?.fieldValue || "";

		return (
			<Input
				label={attributeDefinition.label}
				placeholder={attributeDefinition.placeholderText || attributeDefinition.label}
				required={attributeDefinition.isRequired}
				inputProps={{
					defaultValue: initialValue,
					maxLength: attributeDefinition.isLimited ? attributeDefinition.maxLengthLimit : undefined,
					readOnly: isReadOnly,
					disabled: isReadOnly,
					onChange: e => {
						if (!isReadOnly) {
							fieldValueChanged({
								fieldId: attributeDefinition.id,
								fieldValue: e.target.value,
								fieldType: attributeDefinition.fieldType!,
								fieldLabel: fieldLabel,
								fieldPath: `${attributeDefinition.id}.stringValue`,
							});
						}
					},
				}}
			/>
		);
	}

	private generateNumberField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		const initialValue = preselectedFormValue?.fieldValue || "";

		return (
			<Input
				label={attributeDefinition.label}
				placeholder={attributeDefinition.placeholderText || attributeDefinition.label}
				required={attributeDefinition.isRequired}
				inputProps={{
					type: "number",
					defaultValue: initialValue,
					min: attributeDefinition.minNumberValue,
					max: attributeDefinition.maxNumberValue,
					readOnly: isReadOnly,
					disabled: isReadOnly,
					onChange: e => {
						if (!isReadOnly) {
							const value = parseFloat(e.target.value) || 0;
							fieldValueChanged({
								fieldId: attributeDefinition.id,
								fieldValue: value,
								fieldType: attributeDefinition.fieldType!,
								fieldLabel: fieldLabel,
								fieldPath: `${attributeDefinition.id}.doubleValue`,
							});
						}
					},
				}}
			/>
		);
	}

	private generateBooleanField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		const initialValue = preselectedFormValue?.fieldValue ?? attributeDefinition.defaultState ?? false;

		return (
			<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
				<label style={{ fontSize: "0.9rem" }}>
					{attributeDefinition.label}
					{attributeDefinition.isRequired && <span style={{ color: "red", marginLeft: "0.2rem" }}>*</span>}
				</label>
				<ToggleSlider
					isActive={initialValue}
					disabled={isReadOnly}
					onChange={value => {
						if (!isReadOnly) {
							fieldValueChanged({
								fieldId: attributeDefinition.id,
								fieldValue: value,
								fieldType: attributeDefinition.fieldType!,
								fieldLabel: fieldLabel,
								fieldPath: `${attributeDefinition.id}.boolValue`,
							});
						}
					}}
				/>
			</div>
		);
	}

	private generateDropdownField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		const items = this.generateDropdownItemsFromAttributeDefinition(attributeDefinition);
		const selectedItem = this.getSelectedDropdownItemFromFormValues(items, preselectedFormValue?.fieldValue || "");

		return (
			<Select
				label={fieldLabel || attributeDefinition.label}
				placeholder={attributeDefinition.placeholderText || "Select option"}
				items={items}
				selected={selectedItem}
				required={attributeDefinition.isRequired}
				onChange={item => {
					if (!isReadOnly && item) {
						fieldValueChanged({
							fieldId: attributeDefinition.id,
							fieldValue: item.key,
							dropdownText: item.label,
							fieldType: attributeDefinition.fieldType!,
							fieldLabel: fieldLabel,
							fieldPath: `${attributeDefinition.id}.selectedOptionIds[0]`,
							selectedChildId: item.key,
						});
					}
				}}
			/>
		);
	}

	private generateConditionalDropdownField(props: DynamicFieldProps): React.ReactNode {
		return <ConditionalDropdown {...props} />;
	}

	private generateSingleSelectField(props: DynamicFieldProps): React.ReactNode {
		// SingleSelect is similar to Dropdown
		return this.generateDropdownField(props);
	}

	private generateDateTimeField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		const initialDate = preselectedFormValue?.fieldValue ? new Date(preselectedFormValue.fieldValue) : new Date();

		return (
			<DatePicker
				label={attributeDefinition.label}
				required={attributeDefinition.isRequired}
				initialDate={initialDate}
				readOnly={isReadOnly}
				onChange={date => {
					if (!isReadOnly) {
						fieldValueChanged({
							fieldId: attributeDefinition.id,
							fieldValue: date,
							fieldType: attributeDefinition.fieldType!,
							fieldLabel: fieldLabel,
							fieldPath: `${attributeDefinition.id}.dateValue`,
						});
					}
				}}
			/>
		);
	}

	private generateTextDescriptionField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		const initialValue = preselectedFormValue?.fieldValue || "";

		return (
			<Input
				label={attributeDefinition.label}
				placeholder={attributeDefinition.placeholderText || attributeDefinition.label}
				required={attributeDefinition.isRequired}
				inputProps={{
					defaultValue: initialValue,
					maxLength: attributeDefinition.isLimited ? attributeDefinition.maxLengthLimit : undefined,
					style: { minHeight: "100px", paddingTop: "0.75rem" },
					readOnly: isReadOnly,
					disabled: isReadOnly,
					onChange: e => {
						if (!isReadOnly) {
							fieldValueChanged({
								fieldId: attributeDefinition.id,
								fieldValue: e.target.value,
								fieldType: attributeDefinition.fieldType!,
								fieldLabel: fieldLabel,
								fieldPath: `${attributeDefinition.id}.stringValue`,
							});
						}
					},
				}}
			/>
		);
	}

	private generateFileField(props: DynamicFieldProps): React.ReactNode {
		const { attributeDefinition, fieldValueChanged, preselectedFormValue, isReadOnly } = props;
		const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

		// Extract file name from preselected value
		// Could be a File object, file name string, or URL string
		const getInitialFiles = (): string[] => {
			if (!preselectedFormValue) {
				return [];
			}

			// First, check if we have a stored file name (for persistence after tab switch)
			if (preselectedFormValue.fileFileName) {
				return [preselectedFormValue.fileFileName];
			}

			if (!preselectedFormValue.fieldValue) {
				return [];
			}

			const value = preselectedFormValue.fieldValue;

			// If it's a File object, use its name
			if (value instanceof File) {
				return [value.name];
			}

			// If it's a string (file name or URL), extract the file name
			if (typeof value === "string") {
				// If it's a URL, extract the file name from the path
				if (value.includes("/") || value.includes("\\")) {
					const fileName = value.split("/").pop() || value.split("\\").pop() || value;
					return [fileName];
				}
				// Otherwise, treat it as a file name
				return [value];
			}

			return [];
		};

		const initialFiles = getInitialFiles();

		// If read-only, show file information
		if (isReadOnly) {
			return (
				<div style={{ padding: "1rem", border: "1px solid #dee1e6", borderRadius: "8px", backgroundColor: "#f5f5f5" }}>
					<label style={{ fontSize: "0.9rem", fontWeight: 500, display: "block", marginBottom: "0.5rem" }}>
						{attributeDefinition.label}
						{attributeDefinition.isRequired && <span style={{ color: "red", marginLeft: "0.2rem" }}>*</span>}
					</label>
					{initialFiles.length > 0 ? (
						<span style={{ fontSize: "0.85rem", color: "#666" }}>{initialFiles[0]}</span>
					) : (
						<span style={{ fontSize: "0.85rem", color: "#666" }}>No file uploaded</span>
					)}
				</div>
			);
		}

		const fileExtensionTypes = (attributeDefinition.fileExtensionTypes || []).map(ext => getFileExtensionTypeString(ext));
		const acceptString = fileExtensionTypes.map(ext => `.${ext.toLowerCase()}`).join(",");
		const maxFileSizeMB = attributeDefinition.fileSize ? attributeDefinition.fileSize / (1024 * 1024) : 24;

		return (
			<FileUploadInput
				label={attributeDefinition.label}
				required={attributeDefinition.isRequired}
				accept={acceptString}
				maxFileSize={maxFileSizeMB}
				initialFiles={initialFiles}
				onFilesSelect={files => {
					if (files.length > 0) {
						// Store both the File object and the file name for persistence
						const file = files[0];
						fieldValueChanged({
							fieldId: attributeDefinition.id,
							fieldValue: file, // Store File object for submission
							fieldType: attributeDefinition.fieldType!,
							fieldLabel: fieldLabel,
							fieldPath: `${attributeDefinition.id}.file`,
							// Store file name separately for persistence when switching tabs
							fileFileName: file.name, // Custom property to store file name
						});
					} else {
						// Clear file selection
						fieldValueChanged({
							fieldId: attributeDefinition.id,
							fieldValue: null,
							fieldType: attributeDefinition.fieldType!,
							fieldLabel: fieldLabel,
							fieldPath: `${attributeDefinition.id}.file`,
							fileFileName: undefined,
						});
					}
				}}
			/>
		);
	}

	private generateDropdownItemsFromAttributeDefinition(attributeDefinition: Attribute): SelectItem[] {
		return (attributeDefinition.childrenOptionDefinitions || []).map((option: Option) => {
			const priceText = option.price ? ` ${this.formatPrice(option.price)}` : "";
			return new SelectItem(option.id || option.value, `${option.value}${priceText}`);
		});
	}

	private getSelectedDropdownItemFromFormValues(items: SelectItem[], selectedValue: string): SelectItem | undefined {
		return items.find(item => item.key === selectedValue);
	}

	private formatPrice(price: Money): string {
		if (!price) return "";
		// Use the Money class's toLocaleString method for proper formatting
		return price.toLocaleString();
	}

	private displayNotImplementedText(fieldType: FieldType): React.ReactNode {
		const fieldTypeName = this.getFieldTypeName(fieldType);
		return <div style={{ padding: "1rem", textAlign: "center", color: "#666" }}>The field type {fieldTypeName} is not implemented yet</div>;
	}

	private getFieldTypeName(fieldType: FieldType): string {
		switch (fieldType) {
			case FieldType.Text:
				return "Text";
			case FieldType.Number:
				return "Number";
			case FieldType.Bool:
				return "Boolean";
			case FieldType.Dropdown:
				return "Dropdown";
			case FieldType.ConditionalDropdown:
				return "Conditional Dropdown";
			case FieldType.ChildDropdown:
				return "Child Dropdown";
			case FieldType.DateTime:
				return "Date Time";
			case FieldType.SingleSelect:
				return "Single Select";
			case FieldType.MultiSelect:
				return "Multi Select";
			case FieldType.TextDescription:
				return "Text Description";
			case FieldType.File:
				return "File";
			case FieldType.Image:
				return "Image";
			default:
				return "Unknown";
		}
	}

	preselectDynamicFieldValue(fieldId: string, formValues: DynamicFieldItem[]): DynamicFieldItem | undefined {
		return formValues.find(element => element.fieldId === fieldId);
	}
}

export default new DynamicFormGenerator();
